import { supabase } from '@/integrations/supabase/client';

/**
 * Validates that the current user has an active, non-expired session.
 * Call this before any mutation (save, update, delete) to prevent
 * stale/outdated pages from making changes.
 *
 * @returns The current session if valid
 * @throws Error if session is missing or expired
 */
export async function requireAuth() {
  const { data: { session }, error } = await supabase.auth.getSession();

  if (error || !session) {
    throw new AuthExpiredError('Your session has expired. Please log in again.');
  }

  // Check if the access token is expired (with 30s buffer)
  const expiresAt = session.expires_at; // unix seconds
  if (expiresAt && Date.now() / 1000 > expiresAt - 30) {
    // Try to refresh
    const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshed.session) {
      throw new AuthExpiredError('Your session has expired. Please log in again.');
    }
    return refreshed.session;
  }

  return session;
}

/** Distinct error class so callers can detect auth failures specifically */
export class AuthExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthExpiredError';
  }
}
